/**
 * Constantes relacionadas con archivos y validaciones
 */

// ==========================================
// TAMAÑOS DE ARCHIVO
// ==========================================

export const FILE_SIZE = {
  /** Máximo por archivo para todos los mecanismos de firma. */
  MAX_SIZE_RSE: 150 * 1024 * 1024,
  MAX_SIZE_RSE_MB: 150,
  /** Alias histórico; mismo tope de 150 MB. */
  MAX_SIZE_OTHER: 150 * 1024 * 1024,
  MAX_SIZE_OTHER_MB: 150,
  /** Techo absoluto. Usar maxSignFileSizeBytes() para firmar. */
  MAX_SIZE: 150 * 1024 * 1024,
  MAX_SIZE_MB: 150,
  CHUNK_SIZE: 1024 * 1024, // 1MB para uploads
  SMALL_FILE: 1024 * 1024, // 1MB
  MEDIUM_FILE: 5 * 1024 * 1024, // 5MB
  LARGE_FILE: 150 * 1024 * 1024,
  /** Tope para envío por correo tras firmar (mensaje + Base64 + límites SMTP). */
  MAX_EMAIL_ATTACHMENT: 5 * 1024 * 1024,
  MAX_EMAIL_ATTACHMENT_MB: 5
} as const;

/** Bytes máximos por archivo (150 MB en todos los mecanismos). */
export function maxSignFileSizeBytes(_mechanism?: string): number {
  return FILE_SIZE.MAX_SIZE;
}

export function maxSignFileSizeMb(_mechanism?: string): number {
  return FILE_SIZE.MAX_SIZE_MB;
}

export function fileTooLargeMessage(maxMb: number): string {
  return `El archivo excede el tamaño máximo permitido (${maxMb}MB)`;
}

// ==========================================
// TIPOS DE ARCHIVO PERMITIDOS
// ==========================================

export const ALLOWED_FILE_TYPES = {
  PDF: {
    mimeTypes: ['application/pdf'],
    extensions: ['.pdf'],
    description: 'Documentos PDF'
  },
  CADES: {
    extensions: ['.p7z', '.p7s', '.p7m'],
    description: 'Archivos firmados CAdES'
  },
  /** XML firmado XAdES Enveloped (solo verificación en UI por ahora). */
  XADES: {
    mimeTypes: ['application/xml', 'text/xml'],
    extensions: ['.xml'],
    description: 'Archivos firmados XAdES (.xml)'
  },
  P10: {
    extensions: ['.p10', '.pfx'],
    mimeTypes: ['application/x-pkcs12', 'application/x-pkcs12'],
    description: 'Certificados digitales (.p10, .pfx)'
  }
} as const;

// ==========================================
// LÍMITES
// ==========================================

export const FILE_LIMITS = {
  MAX_FILES: 10,
  MAX_FILE_NAME_LENGTH: 255,
  MIN_FILE_SIZE: 1, // 1 byte mínimo
} as const;

// ==========================================
// MENSAJES DE ERROR
// ==========================================

export const FILE_ERROR_MESSAGES = {
  // Tamaño
  FILE_TOO_LARGE: `El archivo excede el tamaño máximo permitido (${FILE_SIZE.MAX_SIZE_OTHER_MB}MB)`,
  FILE_TOO_SMALL: 'El archivo está vacío o es demasiado pequeño',
  
  // Tipo
  INVALID_FILE_TYPE: 'Tipo de archivo no permitido',
  INVALID_PDF: 'El archivo no es un PDF válido',
  INVALID_CADES: 'El archivo no es un CAdES válido',
  INVALID_XADES: 'El archivo no es un XAdES válido (.xml)',
  INVALID_VERIFY_TYPE: 'Solo se permiten PDF firmados, CAdES (.p7z, .p7m) o XAdES (.xml)',
  INVALID_P12: 'El certificado no es válido',
  
  // Cantidad
  NO_FILES_SELECTED: 'Por favor selecciona al menos un archivo',
  TOO_MANY_FILES: `Máximo ${FILE_LIMITS.MAX_FILES} archivos permitidos`,
  
  // Lectura
  FILE_READ_ERROR: 'Error al leer el archivo',
  FILE_CORRUPTED: 'El archivo parece estar corrupto',
  
  // Carga
  UPLOAD_FAILED: 'Error al cargar el archivo',
  UPLOAD_TIMEOUT: 'La carga del archivo tardó demasiado tiempo',
  
  // Red
  NETWORK_ERROR: 'Error de conexión. Verifica tu internet.',
  SERVER_ERROR: 'Error del servidor. Intenta nuevamente más tarde.',
  
  // Firma
  SIGN_FAILED: 'No se pudieron firmar los documentos.',
  SIGN_ERROR: 'Hubo un error al firmar los documentos.',
  SIGN_NO_FILES: 'Por favor, selecciona al menos un archivo para firmar.',
  
  // Verificación
  VERIFY_FAILED: 'Hubo un error al verificar la firma. Por favor intenta nuevamente.',
  VERIFY_NO_FILE: 'Por favor selecciona un archivo para verificar.',
  VERIFY_NO_SIGNATURES: 'No se encontraron firmas en el documento.',
  
  // Descarga
  DOWNLOAD_ERROR: 'Ocurrió un error al descargar el archivo.',
  DOWNLOAD_NO_FILE: 'No se ha seleccionado ningún archivo para descargar.',
  
  // Credenciales
  INVALID_CREDENTIALS: 'Revise sus credenciales y/o intente nuevamente.',
  MISSING_CREDENTIALS: 'Por favor, ingrese el número de identificación y la contraseña.',
  MISSING_CERTIFICATE: 'Por favor, seleccione un certificado y digite la contraseña.'
} as const;

// ==========================================
// MENSAJES DE ÉXITO
// ==========================================

export const FILE_SUCCESS_MESSAGES = {
  FILES_UPLOADED: 'Archivos cargados exitosamente',
  FILES_SIGNED: 'Archivos firmados exitosamente',
  FILE_DOWNLOADED: 'Archivo descargado',
  FILES_DOWNLOADED: 'Archivos descargados',
  SIGNATURE_VERIFIED: 'Firma verificada correctamente',
  SIGNATURES_VERIFIED: 'Firmas verificadas correctamente'
} as const;

// ==========================================
// EXTENSIONES DE ARCHIVO
// ==========================================

export const FILE_EXTENSIONS = {
  PDF: '.pdf',
  P7Z: '.p7z',
  P7S: '.p7s',
  P7M: '.p7m',
  XML: '.xml',
  P12: '.p12',
  PFX: '.pfx'
} as const;

// ==========================================
// ICONOS POR TIPO DE ARCHIVO
// ==========================================

export const FILE_ICONS = {
  PDF: 'fas fa-file-pdf',
  CADES: 'fas fa-file-signature',
  XADES: 'fas fa-file-code',
  P12: 'fas fa-certificate',
  GENERIC: 'fas fa-file',
  FOLDER: 'fas fa-folder',
  DOWNLOAD: 'fas fa-download',
  UPLOAD: 'fas fa-upload'
} as const;

// ==========================================
// COLORES POR TIPO DE ARCHIVO
// ==========================================

export const FILE_COLORS = {
  PDF: '#dc2626', // Rojo
  CADES: '#3b82f6', // Azul
  XADES: '#059669', // Verde
  P12: '#f59e0b', // Amarillo/Dorado
  GENERIC: '#6b7280' // Gris
} as const;

